import React from 'react';
import { Row, Col, Empty, Spin, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { atom, useAtom } from "jotai";
import { dateRangeState, FlexBox, TextCommon, TableCustom } from '@esg/ui';
import { useHttpQuery } from '@esg/framework';
import { agencyApis } from '@esg/business-agency';
import { BranchProfileCard } from '../../../../Components';

interface CustomerDetailContainerProps {
    customerId: string;
    branchId: string; 
    onBack: () => void;
}

const customerPaginationAtom = atom<Record<string, number>>({});

const CustomerDetailContainer: React.FC<CustomerDetailContainerProps> = ({ customerId, branchId, onBack }) => {
    const pageSize = 2;
    const [dateRange] = useAtom(dateRangeState);

    const stateKey = `${customerId}-${dateRange?.[0]?.toISOString() || 'start'}-${dateRange?.[1]?.toISOString() || 'end'}`;
    
    const [paginationState, setPaginationState] = useAtom(customerPaginationAtom);
    const currentPage = paginationState[stateKey] || 1;

    const setCurrentPage = (page: number) => {
        setPaginationState((prev) => ({ ...prev, [stateKey]: page }));
    };

    const { data: branchesResponse, isLoading: isBranchesLoading } = useHttpQuery(
        agencyApis.branch.getBranches, 
        { query: { pageSize: 200 } } 
    );

    const { data: appointmentsResponse, isLoading: isAppointmentsLoading } = useHttpQuery(
        agencyApis.appointment.getAppointments, 
        {
            query: {
                ...(dateRange?.[0] && { startTime: dateRange[0].toISOString() }),
                ...(dateRange?.[1] && { endTime: dateRange[1].toISOString() }),
                customerId: customerId,
                pageSize: 200 
            }
        },
        { enabled: !!customerId }
    );
    
    const safeBranches = branchesResponse?.items || [];
    const safeAppointments = appointmentsResponse?.items || [];

    const isPageLoading = isBranchesLoading || isAppointmentsLoading;

    const validAppointments = safeAppointments.filter((apt: any) => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return true;
        const aptDate = new Date(apt.appointmentDate).getTime();
        const startSelection = dateRange[0].toDate().getTime(); 
        const endSelection = dateRange[1].toDate().getTime();
        
        return aptDate >= startSelection && aptDate <= endSelection;
    });

    const startIndex = (currentPage - 1) * pageSize;
    const paginatedAppointments = validAppointments.slice(startIndex, startIndex + pageSize);

    const mergedProfiles = paginatedAppointments.map((apt: any, index: number) => {
        const currentAptBranchId = apt.branchId || branchId; 
        const branchInfo = safeBranches.find((b: any) => b.id === currentAptBranchId); 

        return {
            id: `${apt.customerId}-${apt.appointmentDate}-${index}`, 
            branchName: branchInfo?.name || 'Unknown Branch',
            phoneNumber: branchInfo?.phoneNumber || 'N/A',
            country: branchInfo?.country || 'N/A',
            state: branchInfo?.state || 'N/A',
            city: branchInfo?.city || 'N/A',
            timeZone: branchInfo?.timezone || 'N/A', 
            postcode: branchInfo?.postalCode || 'N/A',
            branchAddress: branchInfo?.address || 'N/A',
            mainBusinessCategory: Array.isArray(branchInfo?.category) && branchInfo.category.length > 0 
                ? branchInfo.category 
                : [],
            
            fullName: customerId ? `Customer ID: ${customerId}` : 'Unknown Customer',
            email: 'N/A',
            appointmentTime: apt.appointmentDate, 
            businessName: 'N/A',
        };
    });

    const tableColumns = [
        {
            key: 'grid-wrapper',
            render: (_: any, record: any) => (
                <FlexBox width="100%" padding="4px">
                    <Row gutter={[16, 16]} style={{ width: '100%', margin: 0 }}>
                        {record.cards.map((profile: any) => (
                            <Col xs={24} sm={24} md={24} lg={12} xl={12} key={profile.id}>
                                <BranchProfileCard branch={profile} />
                            </Col>
                        ))}
                    </Row>
                </FlexBox>
            )
        }
    ];

    const tableDataSource = [{
        id: `page-${currentPage}`,
        cards: mergedProfiles
    }];

    return (
        <FlexBox direction="column" width="100%" gap={16}>
            
            <FlexBox direction="row" alignItems="center" gap={12} width="100%">
                <Button 
                    type="text" 
                    icon={<ArrowLeftOutlined />} 
                    onClick={onBack}
                />
                <TextCommon fontSize="16px" fontWeight={600} color="#262626">
                    Customer Appointment Profiles
                </TextCommon>
            </FlexBox>

            {isPageLoading ? (
                <FlexBox direction="row" justifyContent="center" alignItems="center" padding="48px" height="200px" width="100%">
                    <Spin size="large" tip="Loading customer profiles from server..." />
                </FlexBox>
            ) : validAppointments.length === 0 ? (
                <FlexBox direction="row" justifyContent="center" alignItems="center" padding="48px" height="200px" width="100%">
                    <Empty description="No appointments found for this customer in the selected date range." />
                </FlexBox>
            ) : (
                <TableCustom 
                    columns={tableColumns}
                    dataSource={tableDataSource}
                    showHeader={false} 
                    showFooter={true} 
                    pageInfo={{
                        page: currentPage,
                        pageSize: pageSize,
                        totalItems: validAppointments.length,
                        pageCount: Math.ceil(validAppointments.length / pageSize)
                    }}
                    onPageChange={(page) => setCurrentPage(page)}
                />
            )}
        </FlexBox>
    );
};

export { CustomerDetailContainer };